// lib
import React, { useContext, useReducer, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { truncate, isEqual } from 'lodash'

import { filtersReducer } from '@aytm/app/stats/page/sidebar/filters/reducers'
import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/components/context'

import useEventListener from '@aytm/app/stats/page/components/hooks/useEventListener'
import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'
import { usePrevious } from '@aytm/app/insights/dashboard/components/hooks'

import SchemaRules from '@aytm/app/stats/lib/query/schema-rules'
import BasicFilters from '@aytm/app/stats/lib/query/basic-filters'
import containerExpander from '@aytm/lib/layout/container-expander'
import {
  changeBasicValue,
  deleteBasicValue,
  deleteCascadeBasicValue,
  setAdvancedValue,
  setBasicValue,
  switchMode,
  updateCascadeBasicValue
} from '@aytm/app/stats/page/sidebar/filters/actions'
import { getLogicData } from '@aytm/app/stats/lib/query/refs'
import { convertValuesToTree, convertTreeToValues } from '@aytm/app/stats/lib/util/converter'
import { renderFilters, renderSurveySchema } from '@aytm/app/stats/page/sidebar/filters/converter'
import { isFilterApplied } from '@aytm/app/stats/page/components/lib/utils'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// components
import { Button } from 'primereact/button'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'
import SidebarPopupWarning from '@aytm/app/stats/page/sidebar/components/SidebarPopupWarning'
import FiltersGroup from '@aytm/app/stats/page/sidebar/filters/FiltersGroup'
import FiltersGroupCompletes from '@aytm/app/stats/page/sidebar/filters/groups/FiltersGroupCompletes'
import FiltersAdvanced from '@aytm/app/stats/page/sidebar/filters/FiltersAdvanced'
import FiltersUids from '@aytm/app/stats/page/sidebar/filters/FiltersUids'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'

export const DataContext = React.createContext()

const NO_UIDS = Object.freeze([])

// long enough for the opened group to draw its rows
const REVEAL_WAIT_MS = 2000

const FiltersPanel = ({
  survey,
  model,
  report,
  parser,
  features,
  onDNDStart,
  onDNDEnd,
  addQuestion,
  onVariableUpload,
  onVariableRemove,
  onModeChange,
  onParamsChange,
  onScrollToQuestion,
  getVariableError,
  getVariableProcessing
}) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)

  const {
    filtersMode = 'basic',
    filtersPattern = '',
    loadRejectedCS,
    isAdmin,
    isReportVirtual,
    isReportXpert,
    isQAMode,
    reportUids,
    reportUidsTruncated = false
  } = model

  // a fresh [] per render would hand the uids surface a new identity on every sidebar update
  const uids = reportUids || NO_UIDS

  // The page saves and deletes a heatmap cluster in place, so `survey.assets` keeps its identity across
  // the change; the clusters' content is what tells the catalogue and the clusters to rebuild.
  const clustersKey = JSON.stringify(survey.assets?.['hm.cluster'] || [])

  const filters = useMemo(
    () => renderFilters(survey, model, report, features, isReportXpert),
    [
      survey.country,
      survey.config,
      survey.questions,
      survey.scripts,
      survey.assets,
      clustersKey,
      survey.traits,
      survey.variables,
      survey.pipingVariables,
      survey.customVariables,
      survey.codeBooks,
      JSON.stringify(survey.refsList || {}),
      model.mode,
      // the sentiment and code sub-rows are built from these; without them a catalogue
      // memoised before the tags data arrives never grows them
      model.tags,
      model.tagsData,
      model.isAdmin,
      JSON.stringify(report.current.schema),
      JSON.stringify(report.current.virtual),
      features,
      isReportXpert
    ]
  )

  const surveySchema = useMemo(
    () => renderSurveySchema(survey, model, report, features),
    [
      survey.country,
      survey.questions,
      survey.scripts,
      survey.assets,
      survey.traits,
      survey.variables,
      survey.pipingVariables,
      survey.customVariables,
      survey.codeBooks,
      JSON.stringify(survey.refsList || {}),
      model.tags,
      model.tagsData,
      JSON.stringify(report.current.schema),
      JSON.stringify(report.current.virtual),
      features
    ]
  )

  // a fresh array per content change, so the Basic reading and the parser built on it see the change too
  const clusters = useMemo(() => JSON.parse(clustersKey), [clustersKey])

  const clustersPrev = usePrevious(clusters)
  const surveySchemaPrev = usePrevious(surveySchema)

  // A selection the reader made and did not apply outlives a switch to another tab, but only while the
  // report and the filter it applies are still the ones it was made against. With nothing staged only the
  // mode comes back: rebuilding the editor from its saved Basic tree respells a one-value `in` as `=`, and
  // the panel would offer to apply the filter already in force.
  const saved = sidebarState.filters
  const restored = null != saved?.advanced && saved.reportId === model.reportId && saved.pattern === filtersPattern
  const staged = restored && saved.advanced !== filtersPattern
  const [state, dispatch] = useReducer(
    filtersReducer,
    staged
      ? { mode: saved.mode || filtersMode, basic: saved.basic || null, advanced: saved.advanced }
      : { mode: (restored && saved.mode) || filtersMode, basic: null, advanced: filtersPattern }
  )
  const restoredRef = useRef(restored)

  const [filtersApplying, setFiltersApplying] = useState(false)
  const [filtersReleasing, setFiltersReleasing] = useState(false)

  const questionsCallback = useRef(null)
  const dropdownCallback = useRef(null)

  const showTimerRef = useRef(null)
  const adjustmentTimerRef = useRef(null)

  const componentRef = useRef()
  const dropdownsRef = useRef({})

  const overlayRef = useRef(null)

  const stateRef = useRef()
  stateRef.current = state

  const checkBasicFilters = useRef(true)
  // A Virtual Question's variant decides how its answers read in Basic, so a change to one re-reads the
  // expression: the selection comes back under the new variant, or the panel falls to Advanced when it can't.
  checkBasicFilters.current =
    surveySchemaPrev && clustersPrev
      ? !isEqual(surveySchemaPrev.tags, surveySchema.tags) ||
        !isEqual(surveySchemaPrev.sentiments, surveySchema.sentiments) ||
        !isEqual(surveySchemaPrev.codeBooks, surveySchema.codeBooks) ||
        !isEqual(surveySchemaPrev.customVariables, surveySchema.customVariables) ||
        !isEqual(surveySchemaPrev.virtual, surveySchema.virtual)
      : true

  const [root, setRoot] = useState(null)

  const setComponentRef = useCallback(node => {
    componentRef.current = node
    setRoot(node?.parentNode ?? null)
  }, [])

  const tags = useMemo(() => survey.tags || {}, [survey.tags])
  const codeBooks = useMemo(() => survey.codeBooks || [], [survey.codeBooks])

  const externalData = useMemo(() => [{ key: 'completes', values: [loadRejectedCS || 'completes'] }], [loadRejectedCS])

  const basicFilters = useMemo(
    () =>
      new BasicFilters({
        clusters: clusters,
        surveySchema: surveySchema,
        deepAllowed: true,
        tagsAllowed: true,
        sentimentsAllowed: true,
        isAdmin
      }),
    [surveySchema, clusters, isAdmin]
  )

  const basicFiltersRef = useRef()
  basicFiltersRef.current = basicFilters

  const filtersChangingRef = useRef(false)
  const filtersBasicChangingRef = useRef(false)
  // the expression the panel left Basic for on its own, so it can return once that expression reads back
  const autoAdvancedRef = useRef(null)
  const releasingRef = useRef(false)
  // the mode the reader picked with the toggle; a release keeps Advanced only when they chose it, and
  // returns to Basic when the panel went there on its own (a code-row filter, no Basic reading). The
  // panel remounts on a tab switch or a sidebar redraw, so the choice rides its saved state per report.
  const chosenModeRef = useRef(saved?.reportId === model.reportId ? (saved?.chosenMode ?? null) : null)
  const chosenForRef = useRef(model.reportId)

  const contentContainerExpanderRef = useRef()

  const parserResultEof = useMemo(
    () =>
      parser.analyzeString({
        filter: '',
        saveTokens: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [surveySchema]
  )
  const parserResultPattern = useMemo(
    () =>
      parser.analyzeString({
        filter: state.advanced.trim(),
        clusters: clusters,
        saveTokens: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.advanced, surveySchema, clusters]
  )
  const parserResultPatternPrev = usePrevious(parserResultPattern)

  const parserResultPatternError = useMemo(
    () =>
      0 < stateRef.current.advanced.trim().length &&
      0 < parserResultPattern.expression.status.errors.length &&
      parserResultPattern.expression.status.errors.some(v => !['ref', 'cluster'].includes(v.type)),
    [parserResultPattern]
  )

  const [parserResultPatternBasic, setParserResultPatternBasic] = useState({})

  const parserResultPatternLogicData = useMemo(
    () => getLogicData(tags, codeBooks, parserResultPattern, parserResultPatternError),
    [tags, codeBooks, parserResultPattern, parserResultPatternError]
  )

  // Read only by the Data Lab change listener below, so it maps the rows of the group those questions are
  // listed in: a Virtual Question's options carry the uid of the row they stand for, which is what lets a
  // selection follow its row through a reorder and leave with it on a delete.
  const filtersUIDs = useMemo(() => {
    const questions = filters.find(group => 'virtualQuestions' == group.key)
    if (questions == undefined) return {}

    return null === parserResultPatternBasic
      ? {}
      : Object.keys(parserResultPatternBasic).reduce((acc, cur) => {
          const question = questions.values.find(q => cur == q.key)

          if (question != undefined && question.uid != undefined) {
            acc[question.key] = question.uid
          } else if (question != undefined && question.options.some(option => option.uid != undefined)) {
            acc[cur] = question.options.reduce((_acc, _cur) => {
              _acc[_cur.value] = _cur.uid
              return _acc
            }, {})
          }

          return acc
        }, {})
  }, [filters, parserResultPatternBasic])

  const filtersChanged = useMemo(() => state.advanced != filtersPattern, [state.advanced, filtersPattern])

  const scrollAdjustment = useCallback(
    overlay => {
      const rootRect = root?.getBoundingClientRect()
      const overlayRect = overlay.getBoundingClientRect()
      if (rootRect.top > overlayRect.top || rootRect.bottom < overlayRect.bottom) {
        overlay.scrollIntoView(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [root]
  )

  const scrollCallback = useCallback(
    key => {
      const dropdownNode = dropdownsRef.current[key].current
      const overlay = dropdownNode.getOverlay()
      if (null === overlay) {
        showTimerRef.current = setTimeout(() => dropdownNode?.show(), 100)
      } else {
        scrollAdjustment(overlay)
      }
    },
    [scrollAdjustment]
  )

  const onApplyFilters = useCallback(() => {
    setFiltersApplying(true)

    // a report with no filter stores null, so an emptied filter is written that way and the report
    // compares equal to its saved state again
    onParamsChange({
      filter: state.advanced || null,
      filter_mode: state.mode,
      logicData: parserResultPatternLogicData
    })
  }, [state.advanced, state.mode, parserResultPatternLogicData, onParamsChange])

  const onReleaseActiveFilters = useCallback(() => {
    setFiltersReleasing(true)
    releasingRef.current = true

    // releasing drops the expression, not the editor the reader chose to work in
    onParamsChange({
      filter: null,
      filter_mode: 'advanced' == state.mode && 'advanced' == chosenModeRef.current ? 'advanced' : 'basic',
      logicData: []
    })
  }, [state.mode, onParamsChange])

  // ONCHANGE FILTER HANDLERS
  const onBasicFilterChange = useCallback(
    (filter, _values) => {
      const values =
        null === _values ||
        ('sliderselect' == filter.type &&
          (_values[0] < 1 ||
            _values[1] > filter.options.length ||
            (1 == _values[0] && _values[1] == filter.options.length)))
          ? 'sliderselect' == filter.type && !(1 == _values[0] && _values[1] == filter.options.length)
            ? [1, filter.options.length]
            : []
          : _values
      const treeValue = convertValuesToTree(filter, filter.key, values, 'value')
      if ('dropdown' == filter.type && filter.subkey != undefined) {
        updateCascadeBasicValue(dispatch, filter.key.toUpperCase(), filter.subkey.toUpperCase(), treeValue)
      } else if ('button' == filter.type && state.basic[filter.key.toUpperCase()]) {
        deleteBasicValue(dispatch, filter.key)
      } else {
        if (0 < Object.keys(treeValue[filter.key.toUpperCase()]).length) {
          changeBasicValue(dispatch, treeValue)
        } else deleteBasicValue(dispatch, filter.key)
      }
    },
    [state.basic, dispatch]
  )
  const onResetBasicFilter = useCallback(
    filter => {
      if ('dropdown' == filter.type && filter.subkey != undefined) {
        deleteCascadeBasicValue(dispatch, filter.key.toUpperCase(), filter.subkey.toUpperCase())
      } else {
        deleteBasicValue(dispatch, filter.key)
      }
    },
    [dispatch]
  )
  const onAdvancedFilterChange = useCallback((value = '') => setAdvancedValue(dispatch, value), [dispatch])

  const onShowFilterDropdown = useCallback(
    dropdownNode => {
      const overlay = dropdownNode.getOverlay()
      contentContainerExpanderRef.current.expand(overlay)

      adjustmentTimerRef.current = setTimeout(() => {
        scrollAdjustment(overlay)
      }, 50)

      overlayRef.current = overlay
    },
    [scrollAdjustment]
  )

  const onHideFilterDropdown = useCallback(
    dropdownNode => {
      const overlay = dropdownNode.getOverlay()
      contentContainerExpanderRef.current.unexpand(overlay)

      adjustmentTimerRef.current = setTimeout(() => {
        scrollAdjustment(overlay)
      }, 50)

      overlayRef.current = null
    },
    [scrollAdjustment]
  )

  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'filters',
        state: {
          mode: state.mode,
          basic: state.basic,
          advanced: state.advanced,
          reportId: model.reportId,
          pattern: filtersPattern,
          chosenMode: chosenModeRef.current
        }
      }),
    [state.mode, state.basic, state.advanced, model.reportId, filtersPattern, sidebarDispatch]
  )

  useEffect(() => {
    if ('function' == typeof questionsCallback.current) {
      questionsCallback.current()
    }
    questionsCallback.current = null
  }, [filters])

  useEffect(() => {
    // the mount that restores an unapplied selection keeps it rather than syncing back to the applied filter
    if (!restoredRef.current && stateRef.current.advanced != filtersPattern) {
      setAdvancedValue(dispatch, filtersPattern)
    }
    // the applied mode does not change on a release from Basic, so the effect below never fires
    if (releasingRef.current) {
      releasingRef.current = false
      autoAdvancedRef.current = null
      if (stateRef.current.mode != filtersMode) switchMode(dispatch, filtersMode)
    }
    setFiltersApplying(false)
    setFiltersReleasing(false)
  }, [filtersPattern])

  // another report's panel starts with no choice of the reader's behind it
  useEffect(() => {
    if (chosenForRef.current === model.reportId) return
    chosenForRef.current = model.reportId
    chosenModeRef.current = null
  }, [model.reportId])

  useEffect(() => {
    // an applied mode set from outside (e.g. another report) outranks a switch the panel made on its own
    autoAdvancedRef.current = null
    const keep = restoredRef.current
    restoredRef.current = false
    if (!keep && stateRef.current.mode != filtersMode) {
      switchMode(dispatch, filtersMode)
    }
  }, [filtersMode])

  useEffect(() => {
    if (null !== dropdownCallback.current && dropdownsRef.current[dropdownCallback.current] != undefined) {
      scrollCallback(dropdownCallback.current)
      dropdownCallback.current = null
    }

    if (filtersChangingRef.current) {
      filtersChangingRef.current = false
    } else if (state.basic && !isEqual(state.basic, parserResultPatternBasic)) {
      filtersBasicChangingRef.current = true

      setAdvancedValue(dispatch, basicFiltersRef.current.convertToAdvanced(state.basic))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.basic, parserResultPatternBasic])

  useEffect(() => {
    if (filtersBasicChangingRef.current) {
      filtersBasicChangingRef.current = false

      setParserResultPatternBasic(stateRef.current.basic)

      return
    }

    try {
      const tree = parserResultPatternError
        ? null
        : 0 < stateRef.current.advanced.trim().length
          ? 0 < parserResultPattern.expression.status.errors.length
            ? null
            : parserResultPattern &&
                parserResultPatternPrev &&
                isEqual(parserResultPattern.expression.status, parserResultPatternPrev.expression.status) &&
                !checkBasicFilters.current
              ? parserResultPatternBasic
              : basicFiltersRef.current.convertToBasic(SchemaRules.flatten(parserResultPattern.expression.tree)) || null
          : {}

      filtersChangingRef.current = tree !== parserResultPatternBasic

      setParserResultPatternBasic(tree)
    } catch {
      filtersChangingRef.current = null !== parserResultPatternBasic

      setParserResultPatternBasic(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parserResultPattern, parserResultPatternError])

  useEffect(
    () => (contentContainerExpanderRef.current = containerExpander(componentRef.current, { margin: 0 })),
    [componentRef]
  )

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current)
      if (adjustmentTimerRef.current) clearTimeout(adjustmentTimerRef.current)
    }
  }, [])

  useNonInitialEffect(() => {
    if (null == parserResultPatternBasic) {
      if ('basic' == stateRef.current.mode) {
        root.scroll({
          top: 0,
          left: 0
        })

        autoAdvancedRef.current = stateRef.current.advanced
      }

      filtersChangingRef.current = true

      switchMode(dispatch, 'advanced')
      setBasicValue(dispatch, null)
    } else {
      if (!isEqual(stateRef.current.basic, parserResultPatternBasic)) {
        filtersChangingRef.current = true

        setBasicValue(dispatch, parserResultPatternBasic)
      }
      if (null != autoAdvancedRef.current) {
        // an expression the reader has since edited in Advanced stays where they are working on it
        if ('advanced' == stateRef.current.mode && autoAdvancedRef.current == stateRef.current.advanced) {
          switchMode(dispatch, 'basic')
        }
        autoAdvancedRef.current = null
      }
      if (
        'advanced' == stateRef.current.mode &&
        !stateRef.current.advanced.trim().length &&
        features.advanced_filters.disabled
      ) {
        root.scroll({
          top: 0,
          left: 0
        })

        switchMode(dispatch, 'basic')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parserResultPatternBasic])

  useEventListener(`applyAnswerValue`, event => {
    if (features.filters.disabled || null === parserResultPatternBasic) return

    const filter = filters
      .filter(group => group.key == event.detail.group)
      .flatMap(group => group.values)
      .find(opt => opt.key == event.detail.key && !opt.disabled)
    if (filter == undefined) return

    if (dropdownsRef.current[event.detail.key] && dropdownsRef.current[event.detail.key].current) {
      scrollCallback(event.detail.key)
    } else {
      dropdownCallback.current = event.detail.key
    }

    const values = convertTreeToValues(filter, state.basic[event.detail.key.toUpperCase()], 'value')
    const index = values.findIndex(v =>
      'multiselect' == filter.type ? v == event.detail.value : v.value == event.detail.value
    )

    switchMode(dispatch, 'basic')

    if (0 > index) {
      const option = 'multiselect' == filter.type ? event.detail.value : { state: true, value: event.detail.value }
      onBasicFilterChange(filter, [...values, option])
    } else {
      const _values = [...values]
      if ('multiselect' == filter.type) _values.splice(index, 1)
      else {
        if (true == _values[index].state) _values[index].state = false
        else _values.splice(index, 1)
      }
      onBasicFilterChange(filter, _values)
    }
  })

  // a jump to a question that is not on the report lands on its row in Excluded Questions; the
  // group draws its rows only once it has opened, so a row that is not there yet is waited for,
  // for a short while only, so a row drawn much later never pulls the panel away from the user
  const revealWaitRef = useRef(null)
  const stopRevealWait = useCallback(() => {
    if (null === revealWaitRef.current) return
    revealWaitRef.current.observer.disconnect()
    clearTimeout(revealWaitRef.current.timer)
    revealWaitRef.current = null
  }, [])
  useEffect(() => stopRevealWait, [stopRevealWait])

  useEventListener('revealFilterQuestion', event => {
    const root = componentRef.current
    if (!root) return

    stopRevealWait()

    const selector = `[data-id="${event.detail.key}"]`
    const row = root.querySelector(selector)
    if (row) {
      row.scrollIntoView(false)
      return
    }

    const observer = new MutationObserver(() => {
      const drawn = root.querySelector(selector)
      if (!drawn) return
      stopRevealWait()
      drawn.scrollIntoView(false)
    })
    observer.observe(root, { childList: true, subtree: true })
    revealWaitRef.current = { observer, timer: setTimeout(stopRevealWait, REVEAL_WAIT_MS) }
  })

  useEventListener('applyUserFilter', event => {
    if (features.filters.disabled || features.advanced_filters.disabled) return

    const { pattern, filter } = event.detail

    const currentAdvPart = state.advanced.trim()
    // Escape the externally-supplied pattern so regex metacharacters in the
    // aytm-Logic fragment can't throw SyntaxError or trigger catastrophic backtracking.
    const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regexp = new RegExp(`\\(\\(\\(${escaped}\\)\\)\\)`, 'i')

    let newFilter = ''
    if (!parserResultPatternError && '' != currentAdvPart) {
      newFilter = regexp.test(currentAdvPart)
        ? currentAdvPart.replace(regexp, filter)
        : `${currentAdvPart} ${
            currentAdvPart.endsWith(' and') || currentAdvPart.endsWith(' or') ? '' : 'and'
          } ${filter}`
    } else {
      newFilter = filter
    }

    filtersChangingRef.current = true

    switchMode(dispatch, 'advanced')
    setAdvancedValue(dispatch, newFilter)
  })

  useEventListener('customQuestionRemoved', event => {
    if (null === parserResultPatternBasic) return

    deleteBasicValue(dispatch, event.detail)
  })

  useEventListener('customQuestionChanged', event => {
    if (null === parserResultPatternBasic) return

    if (Object.hasOwn(event.detail, 'variant')) {
      if (filtersApplying) return

      filtersChangingRef.current = true

      setBasicValue(dispatch, null)

      return
    }

    const treeValue = {}

    if (filtersUIDs[event.detail.id]) {
      const basic = stateRef.current.basic[event.detail.id]
      if (basic == undefined) return

      const treeKeys = Object.keys(basic)

      for (const treeKey of treeKeys) {
        const newKey = event.detail.items[filtersUIDs[event.detail.id][treeKey]]
        if (newKey == undefined) continue

        treeValue[newKey] = basic[treeKey]
      }

      if (0 < Object.keys(treeValue).length) {
        if (event.detail.delay) {
          questionsCallback.current = () => changeBasicValue(dispatch, { [event.detail.id]: treeValue })
        } else {
          changeBasicValue(dispatch, { [event.detail.id]: treeValue })
        }
      } else deleteBasicValue(dispatch, event.detail.id)
    } else {
      const items = Object.values(event.detail.items)

      const treeKeys = Object.keys(filtersUIDs).filter(
        key => null !== key.match(new RegExp(`^${event.detail.id}(?!\\d)`, 'gi'))
      )

      if (0 < treeKeys.length) {
        for (const key of treeKeys) {
          const basic = stateRef.current.basic[key]
          if (basic == undefined) continue

          treeValue[key] ||= null

          const item = event.detail.items[filtersUIDs[key]]
          if (item == undefined) continue

          const newKeys = Object.keys(basic).filter(v => Number(v.slice(1)) <= items[0].size)

          if (0 < newKeys.length)
            treeValue[`${event.detail.id}${item.key}`] = newKeys.reduce((acc, cur) => {
              acc[cur] = basic[cur]
              return acc
            }, {})
          else treeValue[`${event.detail.id}${item.key}`] = null
        }

        if (event.detail.delay) {
          questionsCallback.current = () => changeBasicValue(dispatch, treeValue)
        } else {
          changeBasicValue(dispatch, treeValue)
        }
      }
    }
  })

  const dataContextValue = useMemo(() => {
    return {
      root,
      features,
      state,
      externalData,
      dropdownsRef,
      dispatch,
      onDNDStart,
      onDNDEnd,
      addQuestion,
      onVariableUpload,
      onVariableRemove,
      onParamsChange,
      onScrollToQuestion,
      onAdvancedFilterChange,
      onBasicFilterChange,
      onResetBasicFilter,
      onShowFilterDropdown,
      onHideFilterDropdown,
      getVariableError,
      getVariableProcessing
    }
  }, [
    root,
    features,
    state,
    externalData,
    dropdownsRef,
    dispatch,
    onDNDStart,
    onDNDEnd,
    addQuestion,
    onVariableUpload,
    onVariableRemove,
    onParamsChange,
    onScrollToQuestion,
    onAdvancedFilterChange,
    onBasicFilterChange,
    onResetBasicFilter,
    onShowFilterDropdown,
    onHideFilterDropdown,
    getVariableError,
    getVariableProcessing
  ])

  const renderToggle = () => (
    <span className={style['toggle-inner']}>
      <i className="icon-aytmfd-config" />
      <span data-testid="stat-filters-toggle-mode-text" className={style['toggle-text']}>
        {'advanced' == state.mode ? 'Advanced' : 'Basic'}
      </span>
    </span>
  )

  return (
    <>
      <SidebarPopupWarning
        features={
          'basic' == state.mode || isQAMode || features['filters'].disabled
            ? [features['filters']]
            : [features['advanced_filters']]
        }
        isReportVirtual={isReportVirtual}
        isReportXpert={isReportXpert}
        isQAMode={isQAMode}
        onModeChange={onModeChange}
      />

      <div
        ref={setComponentRef}
        data-testid="stat-filters"
        data-mode={state.mode}
        data-disabled={features.filters.disabled || (state.mode === 'advanced' && features.advanced_filters.disabled)}
        className={`${style.component}${
          features.filters.disabled || ('advanced' == state.mode && features.advanced_filters.disabled)
            ? ' ' + style['component-disabled']
            : ''
        }`}
      >
        <div data-testid="stat-filters-title" className={style['component-title']}>
          Filters
        </div>

        {'advanced' != state.mode && features.filters.disabled && features.advanced_filters.disabled ? null : (
          <div
            className={classNames(style.header, {
              [style['header-disabled']]: 'advanced' == state.mode && features.advanced_filters.disabled
            })}
          >
            <div className={style['header-inner']}>
              {'advanced' == state.mode || !features.advanced_filters.disabled ? (
                <span
                  data-testid="stat-filters-toggle-mode"
                  data-mode={state.mode}
                  data-disabled={'advanced' == state.mode && null === parserResultPatternBasic}
                  className={classNames(style.toggle, {
                    [style['toggle-disabled']]: 'advanced' == state.mode && null === parserResultPatternBasic
                  })}
                  {...pressable(
                    'advanced' == state.mode && null === parserResultPatternBasic
                      ? null
                      : () => {
                          chosenModeRef.current = 'advanced' == state.mode ? 'basic' : 'advanced'
                          switchMode(dispatch, chosenModeRef.current)
                        }
                  )}
                >
                  {'advanced' == state.mode && null === parserResultPatternBasic ? (
                    <>
                      <i className="icon-aytmfd-config" />
                      <span className={style['toggle-text']}>
                        Simplify
                        <br />
                        /clear logic
                      </span>
                    </>
                  ) : 'basic' == state.mode &&
                    0 < state.advanced.length &&
                    !features.advanced_filters.hidden &&
                    root ? (
                    <SidebarPopup
                      root={root}
                      help={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: truncate(state.advanced, { length: 500, separator: ' ' })
                          }}
                        />
                      }
                      width="350px"
                      trigger="hover"
                      direction="bottomleft"
                    >
                      {renderToggle()}
                    </SidebarPopup>
                  ) : 'basic' == state.mode && features.advanced_filters.hidden ? null : (
                    renderToggle()
                  )}
                </span>
              ) : null}

              {features.filters.disabled ? null : (
                <>
                  {filtersChanged && !filtersReleasing ? (
                    <Button
                      data-testid="stat-filters-apply-button"
                      data-action={0 < state.advanced.length ? 'apply' : 'clear'}
                      data-disabled={filtersApplying || parserResultPatternError}
                      className="p-button-rounded p-button-blue"
                      label={`${0 < state.advanced.length ? 'Apply' : 'Clear'} filters`}
                      disabled={filtersApplying || parserResultPatternError}
                      onClick={filtersApplying || parserResultPatternError ? null : onApplyFilters}
                    />
                  ) : isFilterApplied(filtersPattern) ? (
                    <Button
                      data-testid="stat-filters-apply-button"
                      data-action="release"
                      data-disabled={filtersApplying || filtersReleasing}
                      className="p-button-rounded p-button-blue"
                      label="Release filters"
                      disabled={filtersApplying || filtersReleasing}
                      onClick={filtersApplying || filtersReleasing ? null : onReleaseActiveFilters}
                    />
                  ) : (
                    <Button
                      data-testid="stat-filters-apply-button"
                      data-action="none"
                      data-disabled={true}
                      className="p-button-rounded p-button-blue p-button-info"
                      label="No filters applied"
                      disabled
                    />
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <DataContext.Provider value={dataContextValue}>
          {'advanced' == state.mode || null === parserResultPatternBasic ? (
            <FiltersAdvanced
              parser={parser}
              surveySchema={surveySchema}
              clusters={clusters}
              parserResultEof={parserResultEof}
            />
          ) : null}

          {isFilterApplied(filtersPattern) &&
          ('advanced' == state.mode || null === parserResultPatternBasic) &&
          isAdmin &&
          !features.filters.disabled ? (
            <FiltersUids key={filtersPattern} uids={uids} uidsTruncated={reportUidsTruncated} />
          ) : null}

          {filters
            .filter(
              filter =>
                ('advanced' == state.mode || null === parserResultPatternBasic ? filter.advanced : filter.basic) !=
                false
            )
            .map(group =>
              'completes' == group.key ? (
                <FiltersGroupCompletes key={group.key} group={group} />
              ) : (
                <FiltersGroup key={group.key} group={group} />
              )
            )}
        </DataContext.Provider>
      </div>
    </>
  )
}

FiltersPanel.propTypes = {
  survey: PropTypes.object,
  model: PropTypes.object,
  report: PropTypes.object,
  parser: PropTypes.any.isRequired,
  features: PropTypes.object,
  onDNDStart: PropTypes.func,
  onDNDEnd: PropTypes.func,
  addQuestion: PropTypes.func,
  onVariableUpload: PropTypes.func,
  onVariableRemove: PropTypes.func,
  onModeChange: PropTypes.func,
  onParamsChange: PropTypes.func,
  onScrollToQuestion: PropTypes.func,
  getVariableError: PropTypes.func,
  getVariableProcessing: PropTypes.func
}

FiltersPanel.displayName = 'FiltersPanel'

export default FiltersPanel
